import { useQuery } from "@tanstack/react-query";
import { getTransfersLive, type TransferAttempt, type TransferBucket } from "../api/transfers";
import { fmtTimeInZone } from "../lib/time";

// Transfers move on the timescale of a consult (a minute or two), so the same
// 10s cadence as the staffing cards is honest.
const REFRESH_MS = 10_000;

const STAGE_LABEL: Record<NonNullable<TransferAttempt["stage"]>, string> = {
  consulting: "talking to buyer",
  conferenced: "in conference",
  handed_off: "handed off",
};

function pct(b: TransferBucket): string {
  return b.completion_pct === null ? "—" : `${b.completion_pct}%`;
}

/** Today's warm transfers per agent, and every transfer in flight right now.
 * Lives on Queue Status because that is where a supervisor is already looking
 * when a transfer goes wrong. */
export function TransfersLiveCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["transfers", "live"],
    queryFn: getTransfersLive,
    refetchInterval: REFRESH_MS,
  });

  if (isLoading) return <div className="text-sm text-gray-500">Loading transfers…</div>;
  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm">
        Could not load today's transfers.
      </div>
    );
  }

  const t = data.total;
  const tz = data.meta.timezone;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-medium">Transfers today</div>
          <div className="text-xs text-gray-500">
            Attempted, merged with the buyer, or lost — per agent, since midnight {tz}.
          </div>
        </div>
        <div className="flex gap-6 text-center">
          <div>
            <div className="text-2xl font-semibold">{t.attempts}</div>
            <div className="text-xs text-gray-500">attempted</div>
          </div>
          <div>
            <div className={`text-2xl font-semibold ${t.merged > 0 ? "text-green-700" : "text-gray-400"}`}>
              {t.merged}
            </div>
            <div className="text-xs text-gray-500">merged</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">{pct(t)}</div>
            <div className="text-xs text-gray-500">completion</div>
          </div>
          <div>
            <div className={`text-2xl font-semibold ${data.in_progress.length > 0 ? "text-brand-dark" : "text-gray-400"}`}>
              {data.in_progress.length}
            </div>
            <div className="text-xs text-gray-500">in progress</div>
          </div>
        </div>
      </div>

      {data.in_progress.length > 0 && (
        <ul className="px-4 py-2 border-b border-gray-100 text-xs text-gray-700 space-y-0.5">
          {data.in_progress.map((a) => (
            <li key={`${a.agent_user_id}-${a.started_at}`}>
              <span className="font-medium">{a.agent_name}</span> → {a.destination_name} ·{" "}
              {a.stage ? STAGE_LABEL[a.stage] : "in progress"} · since{" "}
              {fmtTimeInZone(a.started_at, tz)}
            </li>
          ))}
        </ul>
      )}

      {data.by_agent.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500">No transfers attempted yet today.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Agent</th>
              <th className="px-4 py-2 text-right">Attempted</th>
              <th className="px-4 py-2 text-right">Merged</th>
              <th className="px-4 py-2 text-right">Cancelled</th>
              <th className="px-4 py-2 text-right">No answer</th>
              <th className="px-4 py-2 text-right">No resolution</th>
              <th className="px-4 py-2 text-right">Completion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.by_agent.map((r) => (
              <tr key={r.agent_user_id ?? "op"}>
                <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.attempts}</td>
                <td className="px-4 py-2 text-right tabular-nums text-green-700">{r.merged}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.cancelled}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.no_answer}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${r.unresolved > 0 ? "text-amber-800" : ""}`}>
                  {r.unresolved}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{pct(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
